/**
 * SoldX Bidding Agreement Content
 * 
 * This file contains all the text content for the SoldX Bidding Agreement.
 * Edit this file to update the agreement terms shown in the bid modal and PDF.
 * 
 * Last Updated: March 2026
 */

export interface AgreementClause {
  id: number
  title: string
  text: string
}

// Agreement Header Info
export const AGREEMENT_HEADER = {
  title: "SOLDX BIDDING AGREEMENT",
  intro: (dateDay: string, dateMonth: string, dateYear: string) => 
    `This Bidding Agreement ("Agreement") is made on this ${dateDay} day of ${dateMonth}, 20${dateYear}, by and between:`,
  platformInfo: 'SoldX ("Platform"), an online real estate auction platform, and',
  bidderInfo: (bidderName: string, bidderAddress: string) => 
    `Mr./Ms. ${bidderName || "_______________"} ("Bidder"), residing at ${bidderAddress || "_______________"}.`,
}

// Section 1A: Bidder Details Labels
export const BIDDER_DETAILS = {
  sectionTitle: "1A. BIDDER DETAILS:",
  labels: {
    bidderName: "Bidder Name:",
    address: "Address:",
    auctionDate: "Auction Date:",
    contactDetails: "Contact Details:",
  }
}

// Section 1B: Property Details Labels  
export const PROPERTY_DETAILS = {
  sectionTitle: "1B. PROPERTY DETAILS:",
  intro: "The following property is being listed for bidding on the SoldX Platform:",
  labels: {
    propertyName: "Property Name:",
    propertyAddress: "Property Address:",
    totalArea: "Total Area (Sq. Ft.):",
    startingBid: "Starting Bid Amount:",
    yourBid: "Your Bid Amount:",
  },
  disclaimer: "The Bidder acknowledges that these details are based on the property listing as available on the Platform.",
}

// Agreement Clauses (2-10)
export const AGREEMENT_CLAUSES: AgreementClause[] = [
  {
    id: 2,
    title: "2. ELIGIBILITY",
    text: "The Bidder confirms that they are legally competent to enter into contracts under the Indian Contract Act, 1872.",
  },
  {
    id: 3,
    title: "3. VERIFICATION",
    text: "The Bidder shall provide a real time photograph and valid signature specimen to the Platform prior to bidding. These shall be annexed to this Agreement.",
  },
  {
    id: 4,
    title: "4. BINDING NATURE OF WINNING BID",
    text: "Upon winning an auction, the Bidder is legally bound to complete the transaction, subject to due diligence and verification of property details provided on the Platform.",
  },
  {
    id: 5,
    title: "5. DUE DILIGENCE",
    text: "The Platform shall provide all relevant property documents for inspection. If no discrepancies are found between the property listing on the Platform and the actual property details, the Bidder cannot withdraw from the purchase.",
  },
  {
    id: 6,
    title: "6. PENALTY CLAUSE",
    text: "If the Bidder, after winning an auction and upon successful due diligence, refuses to complete the purchase, the Bidder shall be liable to pay a penalty of 3% of the Winning Bid Amount to Soldx. This is enforceable under Sections 73 & 74 of the Indian Contract Act, 1872.",
  },
  {
    id: 7,
    title: "7. NO UPFRONT MONEY",
    text: "The Platform does not require upfront deposits for bidding. However, the penalty clause remains enforceable post-auction.",
  },
  {
    id: 8,
    title: "8. GOVERNING LAW",
    text: "This Agreement shall be governed by and construed in accordance with the laws of India. Disputes shall be subject to the jurisdiction of courts in Gurugram, Haryana.",
  },
  {
    id: 9,
    title: "9. DISPUTE RESOLUTION",
    text: "Any disputes arising out of this Agreement shall be resolved through arbitration as per the Arbitration and Conciliation Act, 1996.",
  },
  {
    id: 10,
    title: "10. ACCEPTANCE",
    text: "By checking the agreement checkbox below, the Bidder acknowledges and accepts all terms and conditions herein.",
  },
]

// Dispute Resolution Additional Info
export const DISPUTE_RESOLUTION_DETAILS = {
  language: "English",
  arbitrators: "To be mutually appointed",
}

// Penalty Percentage (used in calculations if needed)
export const PENALTY_PERCENTAGE = 3

// Annexure A Labels (for PDF)
export const ANNEXURE_LABELS = {
  title: "ANNEXURE A",
  bidderSignature: "Bidder Signature:",
  companyName: "SOLDX INDIA PVT LTD.",
  stampText: {
    top: "SOLDX INDIA",
    center: "✓",
    bottom: "PVT. LTD.",
    label: "AUTHORIZED",
  }
}

// Checkbox Agreement Text
export const CHECKBOX_AGREEMENT_TEXT = "I have read, understood and agree to the SoldX Bidding Agreement, Terms of Use and Privacy Statement. I understand that upon winning, I am legally bound to complete the transaction."

// Info Alert Text
export const EMAIL_AGREEMENT_INFO = "After submitting your bid, a copy of this agreement will be sent to your registered email address."

// Company Info for Stamp
export const COMPANY_INFO = {
  name: "SOLDX INDIA PVT LTD.",
  jurisdiction: "Gurugram, Haryana",
  country: "India",
}
